import React, { createContext, useContext, useState, useCallback } from "react";
import { isNonNullish } from "utils/isNonNullish";

interface AppHeaderContextProps {
  nav: JSX.Element | undefined;
  setNav: (content: JSX.Element) => void;
  content: JSX.Element | undefined;
  setContent: (content: JSX.Element) => void;
  guides: JSX.Element | undefined;
  setGuides: (guides: JSX.Element) => void;
  reset: () => void;
}

const AppHeaderContext = createContext<AppHeaderContextProps | undefined>(
  undefined,
);

/*
 * Context for setting the app header content
 */
export const AppHeaderProvider: React.FC<{ children: JSX.Element }> = ({
  children,
}) => {
  const [nav, setNav] = useState<JSX.Element>();
  const [content, setContent] = useState<JSX.Element>();
  const [guides, setGuides] = useState<JSX.Element>();
  const reset = useCallback(() => {
    setContent(undefined);
    setNav(undefined);
    setGuides(undefined);
  }, []);

  return (
    <AppHeaderContext.Provider
      value={{ nav, setNav, content, setContent, guides, setGuides, reset }}
    >
      {children}
    </AppHeaderContext.Provider>
  );
};

export const useAppHeader = (): AppHeaderContextProps => {
  const context = useContext(AppHeaderContext);
  if (!context) {
    throw new Error("useAppHeader must be used within an AppHeaderProvider");
  }
  return context;
};

/*
 * Reusable hook for pages to set the app header content
 * @param header - the content to show in the header bar
 * @param nav - optional collapsible side <nav></nav>
 * @param guides - tours to show up next to the help menu
 */
export const useSetAppHeader = ({
  header,
  nav,
  guides,
}: {
  header: JSX.Element;
  nav?: JSX.Element;
  guides?: JSX.Element;
}): void => {
  const { setContent, setGuides, setNav, reset } = useAppHeader();

  React.useEffect(() => {
    setContent(header);
    if (isNonNullish(nav)) {
      setNav(nav);
    }
    if (isNonNullish(guides)) {
      setGuides(guides);
    }

    // resets the header when the component unmounts
    return () => {
      reset();
    };
  }, [setContent, setGuides, reset, setNav, header, nav, guides]);
};
